import { doc, Timestamp } from 'firebase/firestore';
import { firestore } from '../firebase';
import { CalendarLog, MachineSession, Routine, SetDetail, TrainingSessions } from '../datamodels';
import { localDatabase } from '../storage/LocalDatabase';

type StoredWorkout = { id: string; user_id: string; startMs: number; endMs: number | null; machine_sessions: string[] };
type StoredMachineSession = { id: string; user_id: string; training_session_id: string; machineId: string; exercise_name?: string; dateUsedMs: number; sets: SetDetail[] };
type StoredRoutine = Omit<Routine, 'created_at'> & { id: string; createdAtMs: number };

export const createLocalId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

const hydrateWorkout = (row: StoredWorkout): TrainingSessions => ({
  id: row.id,
  user_id: row.user_id,
  start_date: Timestamp.fromMillis(row.startMs),
  end_date: row.endMs == null ? (null as any) : Timestamp.fromMillis(row.endMs),
  machine_sessions: row.machine_sessions,
});

const hydrateMachineSession = (row: StoredMachineSession): MachineSession => ({
  id: row.id,
  user_id: row.user_id,
  training_session_id: row.training_session_id,
  machine_ref: doc(firestore, 'machines', row.machineId),
  exercise_name: row.exercise_name,
  date_used: Timestamp.fromMillis(row.dateUsedMs),
  sets: row.sets,
});

export const workoutRepository = {
  async create(userId: string, preferredId?: string) {
    const id = preferredId || createLocalId();
    const row: StoredWorkout = { id, user_id: userId, startMs: Date.now(), endMs: null, machine_sessions: [] };
    await localDatabase.put('workouts', id, userId, row, row.startMs);
    return hydrateWorkout(row);
  },
  async get(id: string) {
    const entity = await localDatabase.get<StoredWorkout>('workouts', id);
    return entity ? hydrateWorkout(entity.value) : null;
  },
  async list(userId: string) {
    return (await localDatabase.list<StoredWorkout>('workouts', userId)).map(row => hydrateWorkout(row.value));
  },
  async finish(id: string, machineSessions: MachineSession[]) {
    for (const session of machineSessions) await machineSessionRepository.save(session);
    const entity = await localDatabase.get<StoredWorkout>('workouts', id);
    if (!entity) throw new Error(`Workout ${id} was not found locally`);
    const updated = {
      ...entity.value,
      endMs: Date.now(),
      machine_sessions: machineSessions.map(session => session.id),
    };
    await localDatabase.put('workouts', id, updated.user_id, updated, updated.startMs);
  },
  async setMachineSessionIds(id: string, ids: string[]) {
    const entity = await localDatabase.get<StoredWorkout>('workouts', id);
    if (!entity) throw new Error(`Workout ${id} was not found locally`);
    await localDatabase.put('workouts', id, entity.userId, { ...entity.value, machine_sessions: ids }, entity.sortKey);
  },
  async delete(id: string, userId: string) {
    const workout = await this.get(id);
    if (!workout || workout.user_id !== userId) return;
    await Promise.all(workout.machine_sessions.map(msId => localDatabase.delete('machine_sessions', msId)));
    await localDatabase.delete('workouts', id);
  },
};

export const machineSessionRepository = {
  async save(session: MachineSession) {
    const row: StoredMachineSession = {
      id: session.id,
      user_id: session.user_id,
      training_session_id: session.training_session_id,
      machineId: session.machine_ref.id,
      exercise_name: session.exercise_name,
      dateUsedMs: session.date_used.toMillis(),
      sets: session.sets,
    };
    await localDatabase.put('machine_sessions', row.id, row.user_id, row, row.dateUsedMs);
  },
  async get(id: string) {
    const entity = await localDatabase.get<StoredMachineSession>('machine_sessions', id);
    return entity ? hydrateMachineSession(entity.value) : null;
  },
  async list(userId: string) {
    return (await localDatabase.list<StoredMachineSession>('machine_sessions', userId)).map(row => hydrateMachineSession(row.value));
  },
  async listForWorkout(workoutId: string, userId: string) {
    return (await this.list(userId)).filter(row => row.training_session_id === workoutId);
  },
  async delete(id: string) { await localDatabase.delete('machine_sessions', id); },
};

export const routineRepository = {
  async save(userId: string, name: string, sessions: MachineSession[]) {
    const id = createLocalId();
    const createdAtMs = Date.now();
    const value: StoredRoutine = {
      id, user_id: userId, name, createdAtMs,
      machineSessions: sessions.map(ms => ({ machine_ref: ms.machine_ref.id, exercise_name: ms.exercise_name, sets: ms.sets })),
    };
    await localDatabase.put('routines', id, userId, value, createdAtMs);
    return id;
  },
  async get(id: string): Promise<Routine | null> {
    const row = await localDatabase.get<StoredRoutine>('routines', id);
    return row ? { ...row.value, created_at: Timestamp.fromMillis(row.value.createdAtMs) } : null;
  },
  async list(userId: string): Promise<Routine[]> {
    return (await localDatabase.list<StoredRoutine>('routines', userId)).map(row => ({ ...row.value, created_at: Timestamp.fromMillis(row.value.createdAtMs) }));
  },
  async delete(id: string) { await localDatabase.delete('routines', id); },
};

export const calendarRepository = {
  async list(userId: string): Promise<CalendarLog[]> {
    return (await localDatabase.list<CalendarLog>('calendar_logs', userId)).map(row => row.value);
  },
  async save(log: CalendarLog) {
    const id = `${log.user_id}_${log.date}`;
    const saved = { ...log, id };
    await localDatabase.put('calendar_logs', id, log.user_id, saved, new Date(`${log.date}T00:00:00`).getTime());
    return saved;
  },
};

export const statisticsRepository = {
  async save<T>(userId: string, snapshot: T) {
    await localDatabase.put('statistics', userId, userId, snapshot, Date.now());
  },
  async get<T>(userId: string): Promise<T | null> {
    return (await localDatabase.get<T>('statistics', userId))?.value || null;
  },
};

export interface FitnessProfile {
  displayName: string;
  height: number | null;
  weightHistory: Array<{ id: string; date: string; weight: number }>;
  updatedAt: number;
}

export const fitnessProfileRepository = {
  async get(userId: string): Promise<FitnessProfile | null> {
    return (await localDatabase.get<FitnessProfile>('fitness_profiles', userId))?.value || null;
  },
  async save(userId: string, profile: FitnessProfile) {
    await localDatabase.put('fitness_profiles', userId, userId, profile, profile.updatedAt);
  },
};
